import { ErrorType } from "../../models/HttpError";

//Functions
import * as Functions from '../../functions/common';

// Error Tracer
import * as Sentry from '@sentry/react';
import { IResult } from "../../models/HttpResult";

// Utility function for URL construction with proper encoding
export const buildURL = (baseUrl: string, ...pathSegments: (string | number)[]): string => {
    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const encodedSegments = pathSegments.map(segment => encodeURIComponent(String(segment)));
    return [cleanBase, ...encodedSegments].join('/');
};

// Enhanced error handling with context

export const handleApiError = async (error: any, context: string): Promise<ErrorType> => {
    const errorMessage = `Error in ${context}`;
    console.error(errorMessage, error);

    let errorObj: ErrorType;    
    if (error.text) {
        const body = await error.text();
        errorObj = Functions.HttpErrorHandler(body, error);
        Sentry.captureException(errorObj);
        return errorObj;
    }

    errorObj = {
        ErrorCode: 0, // Use a default number or map 'API_ERROR' to a number as required by ErrorType
        ErrorMessage: errorMessage,
        Errors: [error?.message ?? String(error)],
        // Optionally include the original error for debugging
        originalError: error
    } as unknown as ErrorType;

    Sentry.captureException(errorObj);
    return errorObj;
};

// Standard headers builder
export const buildHeaders = (token: string, additionalHeaders: Record<string, string> = {}): HeadersInit => ({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...additionalHeaders
});

// Generic fetch wrapper with retry logic and better error handling
export const apiRequest = async <T>(
    url: string,
    token: string,
    context: string,
    options: RequestInit = {}
): Promise<T> => {
    const requestOptions: RequestInit = {
        ...options,
        headers: buildHeaders(token, options.headers as Record<string, string>)
    };

    try {
        const response = await fetch(url, requestOptions);
        
        if (!response.ok) {
            Sentry.captureException(response);
            throw response;
        }

        const data: IResult<T> = await response.json();
        return data.data;
    } catch (error) {
        const errorObj = await handleApiError(error, context);
        throw errorObj;
    }
};
